import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const Register = () => {
  const { t } = useLanguage();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('customer');
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch departments for selection
    api.get('/departments')
      .then(res => setDepartments(res.data))
      .catch(err => console.warn('Could not fetch departments:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await register({
        full_name: fullName,
        email,
        password,
        role,
        department_id: departmentId ? parseInt(departmentId) : null
      });

      if (user.role === 'customer') {
        navigate('/dashboard');
      } else {
        navigate('/staff-dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-7 col-lg-6">
          <div className="card glass-card border-0 p-4 p-md-5">
            <div className="text-center mb-4">
              <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle p-3 mb-3">
                <i className="bi bi-person-plus fs-2"></i>
              </div>
              <h3 className="brand-font fw-bold mb-1">{t('createAccount')}</h3>
              <p className="text-muted small">{t('registerSubtitle')}</p>
            </div>

            {error && (
              <div className="alert alert-danger d-flex align-items-center small py-2" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>{error}</div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold small text-secondary">{t('fullNameLabel')}</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  placeholder="e.g. John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-secondary">{t('emailLabel')}</label>
                <input
                  type="email"
                  className="form-control bg-light"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold small text-secondary">{t('passwordLabel')}</label>
                <input
                  type="password"
                  className="form-control bg-light"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>

              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold small text-secondary">{t('role')}</label>
                  <select
                    className="form-select bg-light"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="customer">Customer / Citizen</option>
                    <option value="agent">Support Agent</option>
                    <option value="manager">Department Manager</option>
                  </select>
                </div>

                {role !== 'customer' && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small text-secondary">{t('departments')}</label>
                    <select
                      className="form-select bg-light"
                      value={departmentId}
                      onChange={(e) => setDepartmentId(e.target.value)}
                      required
                    >
                      <option value="">{t('chooseDepartment')}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name} ({d.code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary-gradient w-100 py-2.5 mb-3"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    {t('registering')}
                  </>
                ) : (
                  t('registerBtn')
                )}
              </button>
            </form>

            <div className="text-center mt-3 pt-3 border-top">
              <p className="text-muted small mb-0">
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="fw-semibold text-primary text-decoration-none">
                  {t('signInHere')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
